import React from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";

import { ThemeProvider, createTheme } from "@mui/material/styles";

import CssBaseline from "@mui/material/CssBaseline";

import "./styles/global.css";

import Dashboard from "./pages/Dashboard";

import Orders from "./pages/Orders";

import POS from "./pages/POS";

import Login from "./components/Login";


import Layout from "./components/Layout";

import ProtectedRoute from "./components/ProtectedRoute";

import { AuthProvider, useAuth } from "./contexts/AuthContext";

import { ProfileProvider } from "./contexts/ProfileContext";

import OrdersPending from "./pages/OrdersPending";

import OrdersConfirmed from "./pages/OrdersConfirmed";

import OrdersProcessing from "./pages/OrdersProcessing";

import OrdersDelivered from "./pages/OrdersDelivered";

import OrdersFailed from "./pages/OrdersFailed";

import OrdersReturned from "./pages/OrdersReturned";

import OrdersOutForDelivery from "./pages/OrdersOutForDelivery";

import OrderDetails from "./pages/OrderDetails";

import CategorySetup from "./pages/CategorySetup";

import ProductAttributes from "./pages/ProductAttributes";

import ProductList from "./pages/ProductList";

import AddProduct from "./pages/add-product";

import ProductDetails from "./pages/ProductDetails";

import GenerateBarcode from "./pages/GenerateBarcode";

import BulkImport from "./pages/bulk-import";

import RequestRestockList from "./pages/request-restock-list";

import BannerSetup from "./pages/BannerSetup";

import CouponSetup from "./pages/CouponSetup";

import ClearanceSale from "./pages/ClearanceSale";

import DealOfTheDaySetup from "./pages/DealOfTheDaySetup";

import SendNotification from "./pages/SendNotification";

import PushNotificationSetup from "./pages/PushNotificationSetup";

import SupportTicket from "./pages/SupportTicket";

import ContactMessages from "./pages/ContactMessages";

import NewsletterSubscribers from "./pages/NewsletterSubscribers";

import CustomerList from "./pages/CustomerList";

import CustomerReviews from "./pages/CustomerReviews";

import UserOverview from "./pages/UserOverview";

import Analytics from "./pages/Analytics";

import Wallet from "./pages/Wallet";

import WalletBonusSetup from "./pages/WalletBonusSetup";

import CustomerLoyaltyPointReport from "./pages/CustomerLoyaltyPointReport";

import AddNewDelivery from "./pages/AddNewDelivery";

import DeliveryList from "./pages/DeliveryList";

import TransactionReport from "./pages/reports/TransactionReport";

import ProductReport from "./pages/ProductReport";

import OrderReport from "./pages/OrderReport";

import EarningReports from "./pages/reports/EarningReports";

import InhouseSales from "./pages/reports/InhouseSales";
import BusinessSettings from "./pages/BusinessSettings";

import InhouseShop from "./pages/inhouse-shop";

import SeoSettings from "./pages/SeoSettings";

import SystemSettings from "./pages/SystemSettings";

import LoginSettings from "./pages/LoginSettings";

import CustomAdminLogin from "./pages/CustomAdminLogin";

import CustomEmployeeLogin from "./pages/CustomEmployeeLogin";

import ThemeAddons from "./pages/ThemeAddons";

import PaymentMethods from "./pages/PaymentMethods";

import MarketingTools from "./pages/MarketingTools";

import BusinessPages from "./pages/BusinessPages";

import SocialMediaLinks from "./pages/SocialMediaLinks";

import Profile from "./pages/Profile";
import DeliveryEmergencyContact from "./pages/DeliveryEmergencyContact";
import EmployeeRoleSetup from "./pages/EmployeeRoleSetup";
import ThirdPartyConfig from "./pages/ThirdPartyConfig";
import RefundRequests from "./pages/RefundRequests";
import RefundApproved from "./pages/RefundApproved";
import RefundPending from "./pages/RefundPending";
import RefundProcessing from "./pages/RefundProcessing";

import DeliveryWithdraw from "./pages/DeliveryWithdraw";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e3a5f",
    },

    secondary: {
      main: "#2d5a8c",
    },
  },

  typography: {
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",

    fontSize: 14,

    h1: {
      fontSize: "2.2rem",

      fontWeight: 500,
    },

    h2: {
      fontSize: "1.8rem",

      fontWeight: 500,
    },

    h3: {
      fontSize: "1.5rem",

      fontWeight: 500,
    },

    h4: {
      fontSize: "1.3rem",

      fontWeight: 500,
    },

    h5: {
      fontSize: "1.1rem",

      fontWeight: 500,
    },

    h6: {
      fontSize: "1rem",

      fontWeight: 500,
    },

    body1: {
      fontSize: "0.95rem",
    },

    body2: {
      fontSize: "0.875rem",
    },

    button: {
      fontSize: "0.875rem",

      textTransform: "none",
    },

    caption: {
      fontSize: "0.75rem",
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AuthProvider>
        <ProfileProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<LoginWrapper />} />

              <Route path="/admin" element={<CustomAdminLogin />} />

              <Route path="/admin-dashboard" element={<CustomAdminLogin />} />

              <Route path="/employee" element={<CustomEmployeeLogin />} />

              <Route path="/staff-login" element={<CustomEmployeeLogin />} />


              <Route path="/generate-barcode" element={<GenerateBarcode />} />

              <Route path="/" element={<LoginWrapper />} />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Profile />} />
              </Route>

              <Route
                path="/payment-methods"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<PaymentMethods />} />
              </Route>

              <Route
                path="/third-party-config"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<ThirdPartyConfig />} />
              </Route>

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />

                <Route path="pos" element={<POS />} />

                <Route path="orders/:id" element={<OrderDetails />} />

                <Route path="orders/:status" element={<Orders />} />

                <Route path="orders" element={<Orders />} />

                <Route path="orders/pending" element={<OrdersPending />} />

                <Route path="orders/confirmed" element={<OrdersConfirmed />} />

                <Route
                  path="orders/processing"
                  element={<OrdersProcessing />}
                />

                <Route path="orders/delivered" element={<OrdersDelivered />} />

                <Route path="orders/failed" element={<OrdersFailed />} />

                <Route path="orders/returned" element={<OrdersReturned />} />

                <Route
                  path="orders/out-for-delivery"
                  element={<OrdersOutForDelivery />}
                />

                <Route path="orders/canceled" element={<OrdersFailed />} />

                <Route
                  path="third-party-config"
                  element={<ThirdPartyConfig />}
                />

                <Route path="refund-requests" element={<RefundRequests />} />

                <Route
                  path="refund-requests/approved"
                  element={<RefundApproved />}
                />

                <Route
                  path="refund-requests/pending"
                  element={<RefundPending />}
                />

                <Route
                  path="refund-requests/processing"
                  element={<RefundProcessing />}
                />

                <Route
                  path="refund-requests/rejected"
                  element={<OrdersFailed />}
                />

                <Route path="category-setup" element={<CategorySetup />} />

                <Route
                  path="product-attributes"
                  element={<ProductAttributes />}
                />

                <Route path="product-list" element={<ProductList />} />

                <Route path="add-new-product" element={<AddProduct />} />

                <Route path="edit-product" element={<AddProduct />} />

                <Route path="products/:id" element={<ProductDetails />} />

                <Route path="generate-barcode" element={<GenerateBarcode />} />

                <Route path="bulk-import" element={<BulkImport />} />

                <Route
                  path="request-restock-list"
                  element={<RequestRestockList />}
                />

                <Route path="banner-setup" element={<BannerSetup />} />

                <Route path="offers/coupon" element={<CouponSetup />} />

                <Route
                  path="offers/clearance-sale"
                  element={<ClearanceSale />}
                />

                <Route
                  path="deals-of-the-day"
                  element={<DealOfTheDaySetup />}
                />

                <Route
                  path="notifications/send"
                  element={<SendNotification />}
                />

                <Route
                  path="notifications/push-setup"
                  element={<PushNotificationSetup />}
                />
                <Route path="support-ticket" element={<SupportTicket />} />
                <Route path="contact-messages" element={<ContactMessages />} />
                <Route path="newsletter-subscribers" element={<NewsletterSubscribers />} />
                <Route path="customers" element={<CustomerList />} />
                <Route path="customer-reviews" element={<CustomerReviews />} />
                <Route path="user-overview" element={<UserOverview />} />
                <Route path="wallet" element={<Wallet />} />
                <Route
                  path="wallet-bonus-setup"
                  element={<WalletBonusSetup />}
                />
                <Route
                  path="customer-loyalty-point-report"
                  element={<CustomerLoyaltyPointReport />}
                />
                <Route path="analytics" element={<Analytics />} />
                <Route path="seo-settings" element={<SeoSettings />} />
                <Route
                  path="business-settings"
                  element={<BusinessSettings />}
                />
                <Route path="marketing-tools" element={<MarketingTools />} />
                <Route path="profile" element={<Profile />} />
                <Route path="system-settings" element={<SystemSettings />} />
                <Route path="login-settings" element={<LoginSettings />} />
                <Route path="themes-addons" element={<ThemeAddons />} />
                <Route path="add-new-delivery" element={<AddNewDelivery />} />
                <Route path="delivery-list" element={<DeliveryList />} />
                <Route
                  path="delivery-emergency-contact"
                  element={<DeliveryEmergencyContact />}
                />
                <Route
                  path="delivery-withdraw"
                  element={<DeliveryWithdraw />}
                />
                <Route
                  path="reports/earning-reports"
                  element={<EarningReports />}
                />
                <Route
                  path="reports/transaction-report"
                  element={<TransactionReport />}
                />
                <Route
                  path="reports/product-report"
                  element={<ProductReport />}
                />
                <Route path="reports/order-report" element={<OrderReport />} />
                <Route
                  path="reports/inhouse-sales"
                  element={<InhouseSales />}
                />
                <Route path="inhouse-shop" element={<InhouseShop />} />
                <Route path="business-pages" element={<BusinessPages />} />
                <Route
                  path="social-media-links"
                  element={<SocialMediaLinks />}
                />
              </Route>
            </Routes>
          </Router>
        </ProfileProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function LoginWrapper() {
  const { login, isAuthenticated } = useAuth();

  const navigate = useNavigate();

  // Redirect to dashboard if already authenticated

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return <div>Loading...</div>;
  }

  return <Login onLogin={login} />;
}

export default App;
